import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, HostBinding, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordScoreEnum } from 'account-management';
import { StarbaseModalOverlayService } from 'modal-overlay';
import { debounceTime, map, Subscription } from 'rxjs';
import { LOG_IN_EVENT_MESSAGE, LOG_OUT_EVENT_MESSAGE, StarbaseLoginComponent, StarbaseLoginService, WebApiService } from 'starbase-login';
import { StarbaseModalButton, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalTypes } from 'starbase-modal-dialog';
import { StarbaseNotificationSentiment, StarbaseNotificationService } from 'starbase-notification';
import { AppComponent } from '../app.component';
import { AppGlobalsService } from '../services/app-globals.service';
import { SiteDataService } from '../services/site-data.service';
import { UserDataService } from '../services/user-data.service';
import { StarbaseMobileDetectionService } from 'starbase-launcher';
import { ResetPasswordWizardComponent } from 'starbase-wizard';

@Component({
  selector: 'app-login-wrapper',
  templateUrl: './login-wrapper.component.html',
  styleUrls: ['./login-wrapper.component.css']
})
export class LoginWrapperComponent extends StarbaseLoginComponent implements OnInit, AfterViewInit, OnDestroy {
  title: string;
  unlocked = false;

  @ViewChild('loginView', { static: true }) loginView: StarbaseLoginComponent;
  @ViewChild('resendEmailModal', { static: true }) resendEmailModalTemplate: TemplateRef<any>;
  @ViewChild('prePasswordReset', { static: true }) prePasswordResetModalTemplate: TemplateRef<any>;
  @ViewChild('passwordWizard', { static: false }) passwordWizard: ResetPasswordWizardComponent;
  @HostBinding('@routeAnimation') routeAnimation = true;

  private loginStateWrapperSubscription: Subscription;
  private siteInfoSubscription: Subscription;

  appMinimumPwdScore = AppComponent.appMinimumPwdScore;
  appMinimumPwdLength = AppComponent.appMiniumPwdLength;

  useSiteDropdown = false; // If true default siteSelection to 1
  preResetPwdMode = false;
  resetPwdMode = false;
  resetPwdSuccess = false;
  token: string;
  email: string;
  site: string;
  phase2 = false;
  siteValid = false;

  resendEmailAddress = '';

  siteSelection = 0;
  siteCode = '';
  selectedSitePath = '';
  private readonly originalLoginUri: string;
  allSitesInfo: { uid: number, siteName: string, siteLogoPath: string, siteCode: string }[] = [];

  constructor(
    private appGlobals: AppGlobalsService,
    private ewebApi: WebApiService,
    private ehttp: HttpClient,
    private eloginSvc: StarbaseLoginService,
    private siteSvc: SiteDataService,
    private userSvc: UserDataService,
    private notiSvc: StarbaseNotificationService,
    private overlaySvc: StarbaseModalOverlayService,
    private modalSvc: StarbaseModalDialogService,
    private route: ActivatedRoute, mobileDetectionSvc: StarbaseMobileDetectionService, private router: Router) {
    super(eloginSvc, ewebApi, ehttp, mobileDetectionSvc);
    this.title = this.appGlobals.appTitle;
    this.originalLoginUri = '/api/token';
    StarbaseLoginService.loginUri = `${this.originalLoginUri}?site=${this.siteSelection}`;
  }

  ngOnInit() {
    this.loginStateWrapperSubscription = this.eloginSvc.loginState.subscribe(d => {
      const m = this.eloginSvc.getMessage();
      if (d === true) {
        this.logInSuccess(m);
      } else if (d === false && this.unlocked && m === LOG_OUT_EVENT_MESSAGE) {
        this.logOut(m);
      } else {
        this.logInFailure(m);
      }
    }, (e: any) => {
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder('Authentication attempt failed',
            // eslint-disable-next-line @typescript-eslint/dot-notation
            e['friendly'],
            null, null, StarbaseNotificationSentiment.Error));
        console.log(`problem ${e}`);
    });

    this.route.queryParams.subscribe(params => {
      this.token = params.token;
      this.email = params.email;
      this.site = params.site;
    });

    if (this.token && this.email && this.site) {
      this.launchPhase2ResetPwdWizard();
    }
    this.siteInfoSubscription = this.siteSvc.getAllSitesForLogin().subscribe(sd => {
      this.allSitesInfo = sd;
      if (this.site) {
        this.siteSelection = Number.parseInt(this.site);
        this.selectedSitePath = this.allSitesInfo[this.siteSelection - 1].siteLogoPath;
      } else {
        this.selectedSitePath = this.allSitesInfo[0].siteLogoPath;
      }
    },
      (e) => {
        console.error(e);
        this.allSitesInfo = [{ uid: 0, siteName: 'Error', siteLogoPath: '', siteCode: '' }];
        this.siteSelection = 0;
        this.notiSvc.postNotification(
          this.notiSvc.notificationBuilder('Data Error',
            'Sorry, we could not load all required data. Please reload the page and try again.',
            null, 9000, StarbaseNotificationSentiment.Error)
        );
    });
  }
  ngAfterViewInit() {
    this.loginView._firstInputEl.nativeElement.focus();
    if(this.eloginSvc.isLoggedIn()) {
      this.router.navigateByUrl("/home");
    }
  }
  ngOnDestroy() {
    this.loginStateWrapperSubscription.unsubscribe();
    if (this.siteInfoSubscription) { this.siteInfoSubscription.unsubscribe(); }
  }

  siteChange(): void {
    if (this.allSitesInfo.some(s => s.siteCode.toLowerCase() === this.siteCode.toLowerCase())) {
      this.siteSelection = this.allSitesInfo.findIndex(s => s.siteCode.toLowerCase() === this.siteCode.toLowerCase()) + 1;
      this.updateSiteParam();
    }
    else {
      this.siteSelection = 0;
      StarbaseLoginService.loginUri = `${this.originalLoginUri}?site=${this.siteSelection}`;
    }
    if(this.siteCode != "" && this.siteCode){
      this.siteValid = true;
    } else {
      this.siteValid = false;
    }
    if(this.passwordWizard){
      setTimeout(() => (this.passwordWizard.defaultResetPwdPhase1Steps[0].stepValidationFunc(null),this.passwordWizard.validateStep()), 250);

    }
  }

  updateSiteParam(): void {
    this.selectedSitePath = this.allSitesInfo[this.siteSelection - 1].siteLogoPath;
    StarbaseLoginService.loginUri = `${this.originalLoginUri}?site=${this.siteSelection}`;
  }

  logInSuccess(msg?: string): void {
    if (msg === LOG_IN_EVENT_MESSAGE) {
      this.siteSvc.getData(this.siteSvc.getOneUri, this.allSitesInfo[this.siteSelection - 1].uid).subscribe(
        (site) => {
          this.appGlobals.site = site;
        }
      );
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(`Welcome to ${this.title}`,
          `Logged in as ${this.eloginSvc.getDisplayName()}!`,
          null, null, StarbaseNotificationSentiment.Success));
      this.router.navigateByUrl('/home');
    }
  }
  logInFailure(msg?: string): void {
    if (msg === 'Email address must be confirmed before logging in') {
      this.openResendLinkModal(this.email);
    }
    this.notiSvc.postNotification(
      this.notiSvc.notificationBuilder('Authentication failed',
        'Login Failed. Your site code, username, or password did not match our records. Please try again.',
        null, null, StarbaseNotificationSentiment.Error));
  }
  public logOut(msg?: string): void {
    if (this.notiSvc.getCurrentlyNotifying()) { return; }
    this.notiSvc.postNotification(
      this.notiSvc.notificationBuilder(
        'Logged out',
        msg || 'You have logged out of Starbase.',
        null, 3000, StarbaseNotificationSentiment.Warning));
    this.overlaySvc.disableOverlay();
  }

  resetPassword() {
    super.resetPassword();
    this.launchSelfServeResetPwdWizard();
  }

  launchSelfServeResetPwdWizard(): void {
    if (!this.siteCode) {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Site Required',
          'Please enter Site Code and try again.',
          null, null, StarbaseNotificationSentiment.Warning
        )
      );
    } else {

    }

    this.overlaySvc.enableOverlay();
    this.resetPwdMode = true;
  }

  launchPhase2ResetPwdWizard(): void {
    this.phase2 = true;
    this.overlaySvc.enableOverlay();
    this.resetPwdMode = true;
    this.resetPwdSuccess = false;
  }

  cancelWizard(): void {
    this.resetPwdMode = false;
    this.phase2 = false;
    this.email = null;
    this.token = null;
    this.site = null;
    this.overlaySvc.disableOverlay();
  }

  pwdScore = (pwd: string) => this.userSvc.getRequisiteData(this.userSvc.PasswordScoreUri + pwd).pipe(
    debounceTime(1000),
    map(s => s as PasswordScoreEnum)
  )

  triggerEmailOnServer = (email: string) => {
    if (this.siteSelection === 0) {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Authentication failed',
          'Reset Failed. Your site code, or username did not match our records. Please try again.',
          null, null, StarbaseNotificationSentiment.Error
        )
      );
      this.cancelWizard();
    } else {
      return this.userSvc.getRequisiteData(this.userSvc.PasswordResetRequestUri + email + '&site=' + this.siteSelection);
    }
  }

  changePassword = (pwd: string, email: string) => this.userSvc.confirmPasswordResetForSite(this.email, pwd,
    encodeURIComponent(this.token), this.site).pipe(map(() => this.resetPwdSuccess = true))

    openResendLinkModal(email: string): void {
      this.resendEmailAddress = email;
      const resendModal = new StarbaseModalOptions(
        'Resend confirmation email',
        '',
        [
          new StarbaseModalButton(
            'Cancel',
            () => this.modalSvc.closeModal('User canceled modal')
          ),
          new StarbaseModalButton(
            'Send',
            () => {
              this.userSvc.resendConfirmationEmail(this.resendEmailAddress, this.siteSelection).subscribe(
                () => this.notiSvc.postNotification(
                  this.notiSvc.notificationBuilder(
                    'Email sent',
                    `Please check your email for a message from
                     "support@stellarwastesystems.com" and click the link to confirm your account.`
                  )
                ),
                () => this.notiSvc.notificationBuilder(
                  'Error',
                  'Sorry, the confirmation email could not be sent.'
                )
              );
              this.modalSvc.closeModal('User chose to resend');
            }
          )
        ],
        StarbaseModalTypes.Notice,
        this.resendEmailModalTemplate
      );
      this.modalSvc.postModal(resendModal);
    }
}
